import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import type { FiberProcess } from '@/api/productions/types';
import { PageContainer } from '@/components/shared/PageContainer';
import PageTabs from '@/components/shared/PageTabs';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { ProcessesTable } from '../../-components/ProcessesTable';
import { ProcessSheet } from '../../-components/ProcessSheet';
import { ProductionsTable } from '../../-components/ProductionsTable';
import { useProcessesList } from '../../-hooks/useProcessesList';
import { useProductionsList } from '../../-hooks/useProductionsList';
import { productionsListSearch } from '../../-types';

export const Route = createFileRoute('/(client)/productions/(pages)/(list)/')({
  validateSearch: productionsListSearch,
  component: ProductionsListPage,
});

function ProductionsListPage() {
  const { getTranslation: t } = useTranslator({ page: 'productions' });
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const perms = usePermissions()['fiber-forward'];

  const list = useProductionsList(search);
  const processesList = useProcessesList({
    page: search.pPage,
    count: search.pCount,
    filters: search.pFilters,
  });

  const [sheetProcessId, setSheetProcessId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const activeTab = (search as { tab?: string }).tab ?? 'productions';

  function openCreateProcess() {
    setSheetProcessId(null);
    setSheetOpen(true);
  }

  function openEditProcess(p: FiberProcess) {
    setSheetProcessId(p.id);
    setSheetOpen(true);
  }

  function handleFiltersChange(filters: Record<string, string[]>) {
    void navigate({ search: prev => ({ ...prev, filters, page: 0 }) });
  }

  function handlePaginationChange(v: { pageIndex: number; pageSize: number }) {
    void navigate({
      search: prev => ({ ...prev, page: v.pageIndex, count: v.pageSize }),
    });
  }

  return (
    <>
      <PageTabs
        tabs={[
          { key: 'productions', label: t('tab.productions') },
          { key: 'processes', label: t('tab.processes') },
        ]}
        defaultTab='productions'
        param='tab'
      />

      <PageContainer>
        {activeTab === 'productions' ? (
          <ProductionsTable
            productions={list.productions}
            isLoading={list.isLoading}
            search={search}
            totalPages={list.totalPages}
            onView={id =>
              void navigate({
                to: '/productions/$productionId',
                params: { productionId: id },
              })
            }
            {...(perms.canCreate
              ? {
                  onCreate: () => void navigate({ to: '/productions/create' }),
                  onImport: () => {},
                }
              : {})}
            onFiltersChange={handleFiltersChange}
            onPaginationChange={handlePaginationChange}
          />
        ) : (
          <ProcessesTable
            processes={processesList.processes}
            isLoading={processesList.isLoading}
            page={search.pPage}
            count={search.pCount}
            filters={search.pFilters ?? {}}
            onFiltersChange={pFilters =>
              void navigate({
                search: prev => ({ ...prev, pFilters, pPage: 0 }),
              })
            }
            onPaginationChange={v =>
              void navigate({
                search: prev => ({
                  ...prev,
                  pPage: v.pageIndex,
                  pCount: v.pageSize,
                }),
              })
            }
            {...(perms.canEdit ? { onEdit: openEditProcess } : {})}
            {...(perms.canCreate ? { onAdd: openCreateProcess } : {})}
          />
        )}
      </PageContainer>

      <ProcessSheet
        open={sheetOpen}
        processId={sheetProcessId}
        onClose={() => {
          setSheetOpen(false);
          setSheetProcessId(null);
        }}
      />
    </>
  );
}
